import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import DelayBeforeActionModal from "../components/Campaigns/components/DelayBeforeActionModal";
import { Button, Checkbox, Input, Select } from "../components/ui";
import { createTemplate, updateTemplate } from "../store/features/ownerActions";
import { setTemplates } from "../store/slices/templateSlice";
import { ownerSelector, templateSelector, type RootState } from "../store/store";
import {
  fetchEmailIntegration,
  fetchTemplates,
  getInvitationTemplateMaxLength,
  INVITE_MERGE_TOKEN_FIELDS,
  INVITE_MERGE_TOKEN_KEYS,
  normalizeSingleBraceMergeTokens,
} from "../utils";
import {
  DEFAULT_EMAIL_MESSAGE,
  DEFAULT_EMAIL_SUBJECT,
  DEFAULT_INVITATION_MESSAGE,
  DEFAULT_SEND_MESSAGE,
  EMAIL_CHANNEL_REQUIRED_MESSAGE,
  EMAIL_MESSAGE_MAX_LENGTH,
  EMAIL_SEND_WHEN_COPY,
  EMAIL_SUBJECT_MAX_LENGTH,
  LINKEDIN_MESSAGE_MAX_LENGTH,
  LINKEDIN_SEND_WHEN_COPY,
  SEQUENCE_ACTION_OPTIONS,
  SEQUENCE_TEMPLATE_KIND,
  createSequenceStepId,
  getSequenceActionMeta,
  getSequenceStepMessageError,
  getSequenceStepSubjectError,
  getSequenceStepSubtitle,
  getSequenceStepValidationError,
  getSequenceStepsFromTemplate,
  isSequenceTemplate,
  serializeSequencePayload,
  stepSupportsMessage,
  type SequenceActionType,
  type SequenceStep,
} from "../utils/sequenceTemplate";
import {
  clampSequenceDelay,
  formatSequenceDelayLabel,
  getSequenceDelayValidationError,
  SEQUENCE_DELAY_LIMITS,
  SEQUENCE_DELAY_UNIT_OPTIONS,
  type SequenceDelayUnit,
} from "../utils/sequenceDelay";

const SequenceStepMessageField = ({
  actionType,
  value,
  sendWithoutNote = false,
  onChange,
  onSendWithoutNoteChange,
}: {
  actionType: SequenceActionType;
  value: string;
  sendWithoutNote?: boolean;
  onChange: (next: string) => void;
  onSendWithoutNoteChange?: (checked: boolean) => void;
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const noteDraftRef = useRef<string | null>(null);
  const isInvitation = actionType === "sendConnectionRequest";
  const isEmail = actionType === "sendEmail";
  const withoutNote = isInvitation && sendWithoutNote;
  const tokens = INVITE_MERGE_TOKEN_FIELDS;
  const tokenKeys = INVITE_MERGE_TOKEN_KEYS;
  const maxLength = isInvitation
    ? getInvitationTemplateMaxLength(value)
    : isEmail
      ? EMAIL_MESSAGE_MAX_LENGTH
      : LINKEDIN_MESSAGE_MAX_LENGTH;
  const displayError = getSequenceStepMessageError({
    id: "live",
    actionType,
    delayDays: 0,
    delayUnit: "days",
    continueIfNoReply: true,
    subject: "",
    message: value,
    sendWithoutNote: withoutNote,
  });

  const insertToken = (tokenKey: string) => {
    if (withoutNote) return;
    const inner = String(tokenKey || "")
      .trim()
      .replace(/^\{\{?/, "")
      .replace(/\}\}?$/g, "");
    const tokenText = "{{" + inner + "}}";
    const current = value || "";
    const start = textareaRef.current?.selectionStart ?? current.length;
    const end = textareaRef.current?.selectionEnd ?? current.length;
    const next = normalizeSingleBraceMergeTokens(
      `${current.slice(0, start)}${tokenText}${current.slice(end)}`,
      tokenKeys
    );
    const capped = isInvitation ? next.slice(0, getInvitationTemplateMaxLength(next)) : next;
    onChange(capped);
    const nextCursor = start + tokenText.length;
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const handleSendWithoutNoteChange = (checked: boolean) => {
    if (!onSendWithoutNoteChange) return;
    if (checked) {
      noteDraftRef.current = value;
      onChange("");
    } else {
      const restored = String(noteDraftRef.current || "").trim();
      noteDraftRef.current = null;
      onChange(restored || DEFAULT_INVITATION_MESSAGE);
    }
    onSendWithoutNoteChange(checked);
  };

  return (
    <div>
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted">
        {isEmail ? "Email body" : "Message content"}
      </p>
      {isInvitation ? (
        <>
          <div className="mb-3 rounded-[8px] border border-brand-blue/60 bg-bg-light-blue px-3 py-2.5 text-[11px] text-brand-blue">
            <p>
              LinkedIn&apos;s recent updates limit some non-premium accounts to sending only 5-10 personalized invites
              each month.
            </p>
            <p className="mt-1.5">
              If a sending account can&apos;t send a personalized note, the invitation is sent without a note.
            </p>
          </div>
          <div className="mb-3">
            <Checkbox
              checked={sendWithoutNote}
              onChange={handleSendWithoutNoteChange}
              label="Send without note"
              labelClassName="text-[11px] font-medium text-dark-blue"
            />
          </div>
        </>
      ) : (
        <p className="mb-2 text-[11px] text-muted">
          {isEmail
            ? EMAIL_SEND_WHEN_COPY
            : "Message content is configured here."}
        </p>
      )}
      <div className="relative">
        {!withoutNote ? (
          <span
            className={`absolute right-3 top-2 text-[10px] ${displayError ? "text-danger" : "text-muted"}`}
          >
            {value.length}/{maxLength}
          </span>
        ) : null}
        <textarea
          ref={textareaRef}
          disabled={withoutNote}
          className={[
            "min-h-[130px] w-full resize-y rounded-[8px] border border-line px-3 py-2 text-sm text-dark-blue outline-none placeholder:text-muted focus:border-brand-blue focus:ring-1 focus:ring-brand-blue",
            withoutNote ? "cursor-not-allowed bg-background-grey text-muted" : "pt-7",
          ].join(" ")}
          value={value}
          maxLength={withoutNote ? undefined : maxLength}
          onChange={(event) => {
            const next = event.target.value;
            if (isInvitation) {
              onChange(next.slice(0, getInvitationTemplateMaxLength(next)));
              return;
            }
            onChange(isEmail ? next.slice(0, EMAIL_MESSAGE_MAX_LENGTH) : next);
          }}
          onBlur={() => {
            if (withoutNote) return;
            const normalized = normalizeSingleBraceMergeTokens(value, tokenKeys);
            if (normalized !== value) onChange(normalized);
          }}
          placeholder={
            withoutNote
              ? "Invitation will be sent without a personalized note."
              : isInvitation
                ? "Write your connection request message..."
                : isEmail
                  ? "Write the email body..."
                  : "Type your message here..."
          }
        />
      </div>
      {!withoutNote && displayError ? (
        <p className="mt-1 text-[11px] text-danger">{displayError}</p>
      ) : null}
      <div
        className={[
          "mt-2 flex flex-wrap gap-1.5",
          withoutNote ? "pointer-events-none opacity-50" : "",
        ].join(" ")}
      >
        {tokens.map((token) => (
          <button
            key={token.key}
            type="button"
            disabled={withoutNote}
            className="rounded-[8px] border border-line px-2 py-1 text-[10px] text-dark-blue hover:bg-paper disabled:cursor-not-allowed"
            title={token.help}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => insertToken(token.key)}
          >
            {token.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const resolveWorkspaceId = (currentWorkspace: unknown): string | undefined => {
  if (typeof currentWorkspace !== "string") {
    return (currentWorkspace as { _id?: string } | null)?._id;
  }
  try {
    return JSON.parse(currentWorkspace || "null")?._id as string | undefined;
  } catch {
    return undefined;
  }
};

const SequenceTemplateEditor = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const dispatch: ThunkDispatch<RootState, undefined, Action> = useDispatch();
  const { templates, loading } = useSelector(templateSelector);
  const { owner, currentWorkspace, currentUser } = useSelector(ownerSelector);
  const menuRef = useRef<HTMLDivElement>(null);

  const isCreate = !templateId || templateId === "create";
  const workspaceId = resolveWorkspaceId(currentWorkspace);
  const userId = currentUser?.userId as string | undefined;
  const ownerId = owner?._id as string | undefined;

  const existingTemplate = useMemo(() => {
    if (isCreate || !Array.isArray(templates)) return null;
    return (
      templates.find(
        (template: any) =>
          String(template?._id) === String(templateId) && isSequenceTemplate(template)
      ) || null
    );
  }, [isCreate, templateId, templates]);

  const [name, setName] = useState("");
  const [steps, setSteps] = useState<SequenceStep[]>([]);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hydratedId, setHydratedId] = useState<string | null>(null);
  const [delayModalStepId, setDelayModalStepId] = useState<string | null>(null);
  const [pendingDelay, setPendingDelay] = useState<{
    delayDays: number;
    delayUnit: SequenceDelayUnit;
  } | null>(null);
  const [isPendingDelayModalOpen, setIsPendingDelayModalOpen] = useState(false);
  const [emailChannelVerified, setEmailChannelVerified] = useState(false);

  useEffect(() => {
    if (isCreate) {
      if (hydratedId === "create") return;
      setName("");
      setSteps([]);
      setSelectedStepId(null);
      setPendingDelay(null);
      setIsPendingDelayModalOpen(false);
      setDelayModalStepId(null);
      setHydratedId("create");
      return;
    }
    if (!existingTemplate) return;
    if (hydratedId === String(existingTemplate._id)) return;
    setName(String(existingTemplate.name || existingTemplate.templateName || ""));
    const nextSteps = getSequenceStepsFromTemplate(existingTemplate).map((step, index) =>
      index === 0 ? { ...step, delayDays: 0, delayUnit: "days" as const } : step
    );
    setSteps(nextSteps);
    setSelectedStepId(nextSteps[0]?.id || null);
    setPendingDelay(null);
    setIsPendingDelayModalOpen(false);
    setDelayModalStepId(null);
    setHydratedId(String(existingTemplate._id));
  }, [existingTemplate, hydratedId, isCreate]);

  useEffect(() => {
    if (isCreate || existingTemplate) return;
    if (!Array.isArray(templates) || templates.length === 0) return;
    if (hydratedId && hydratedId !== "create") return;
    toast.error("Sequence template not found");
    navigate("/templates", { replace: true });
  }, [existingTemplate, hydratedId, isCreate, navigate, templates]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      setIsMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isMenuOpen]);

  useEffect(() => {
    let cancelled = false;
    const loadEmailChannel = async () => {
      if (!ownerId && !userId && !workspaceId) return;
      try {
        const status = await fetchEmailIntegration({ ownerId, userId, workspaceId });
        if (!cancelled) setEmailChannelVerified(Boolean(status.verified));
      } catch {
        if (!cancelled) setEmailChannelVerified(false);
      }
    };
    void loadEmailChannel();
    return () => {
      cancelled = true;
    };
  }, [ownerId, userId, workspaceId]);

  const selectedStep = steps.find((step) => step.id === selectedStepId) || null;
  const selectedMeta = selectedStep ? getSequenceActionMeta(selectedStep.actionType) : null;
  const delayModalStep = steps.find((step) => step.id === delayModalStepId) || null;
  const selectedDelayError =
    selectedStep && steps[0]?.id !== selectedStep.id
      ? getSequenceDelayValidationError(String(selectedStep.delayDays), selectedStep.delayUnit, {
          allowZero: true,
        })
      : "";
  const usedInvitation = steps.some((step) => step.actionType === "sendConnectionRequest");
  const menuOptions = SEQUENCE_ACTION_OPTIONS.filter(
    (option) => option.repeatable || !usedInvitation || option.actionType !== "sendConnectionRequest"
  );

  const updateStep = (stepId: string, patch: Partial<SequenceStep>) => {
    setSteps((current) =>
      current.map((step) => (step.id === stepId ? { ...step, ...patch } : step))
    );
  };

  const addAction = (actionType: SequenceActionType) => {
    if (actionType === "sendEmail" && !emailChannelVerified) {
      toast.info(EMAIL_CHANNEL_REQUIRED_MESSAGE);
      setIsMenuOpen(false);
      return;
    }
    const inheritedDelay =
      steps.length === 0
        ? { delayDays: 0, delayUnit: "days" as const }
        : pendingDelay || { delayDays: 0, delayUnit: "days" as const };
    const next: SequenceStep = {
      id: createSequenceStepId(),
      actionType,
      delayDays: inheritedDelay.delayDays,
      delayUnit: inheritedDelay.delayUnit,
      continueIfNoReply: true,
      subject: actionType === "sendEmail" ? DEFAULT_EMAIL_SUBJECT : "",
      message:
        actionType === "sendConnectionRequest"
          ? DEFAULT_INVITATION_MESSAGE
          : actionType === "sendInMail"
            ? DEFAULT_SEND_MESSAGE
            : actionType === "sendEmail"
              ? DEFAULT_EMAIL_MESSAGE
              : "",
      sendWithoutNote: false,
    };
    setSteps((current) => [...current, next]);
    setSelectedStepId(next.id);
    setPendingDelay(null);
    setIsPendingDelayModalOpen(false);
    setIsMenuOpen(false);
  };

  const addDelay = () => {
    setIsMenuOpen(false);
    const anchor = selectedStep || steps[steps.length - 1] || null;
    if (!anchor) {
      toast.info("Add an action first, then use Delay before the next step.");
      return;
    }
    if (anchor.actionType === "sendConnectionRequest") {
      toast.info("Delay is not supported directly after Invitation.");
      return;
    }
    const anchorIndex = steps.findIndex((step) => step.id === anchor.id);
    const nextStep = anchorIndex >= 0 ? steps[anchorIndex + 1] : undefined;
    if (nextStep) {
      setIsPendingDelayModalOpen(false);
      setSelectedStepId(nextStep.id);
      setDelayModalStepId(nextStep.id);
      return;
    }
    setDelayModalStepId(null);
    setIsPendingDelayModalOpen(true);
  };

  const removeStep = (stepId: string) => {
    setSteps((current) => {
      const remaining = current.filter((step) => step.id !== stepId);
      if (remaining[0]) remaining[0] = { ...remaining[0], delayDays: 0, delayUnit: "days" };
      return remaining;
    });
    setSelectedStepId((current) => (current === stepId ? null : current));
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Template name cannot be empty");
      return;
    }
    if (steps.length === 0) {
      toast.error("Add at least one action before saving");
      return;
    }

    const normalizedSteps = steps.map((step, index) =>
      index === 0 ? { ...step, delayDays: 0, delayUnit: "days" as const } : step
    );
    const invalidStep = normalizedSteps.find((step, index) =>
      getSequenceStepValidationError(step, { isFirstStep: index === 0 })
    );
    if (invalidStep) {
      setSelectedStepId(invalidStep.id);
      toast.error(
        getSequenceStepValidationError(invalidStep, {
          isFirstStep: normalizedSteps[0]?.id === invalidStep.id,
        }) || "Fix the selected action before saving"
      );
      return;
    }

    if (normalizedSteps.some((step) => step.actionType === "sendEmail") && !emailChannelVerified) {
      toast.error(EMAIL_CHANNEL_REQUIRED_MESSAGE);
      return;
    }

    const content = {
      kind: SEQUENCE_TEMPLATE_KIND,
      subject: SEQUENCE_TEMPLATE_KIND,
      body: serializeSequencePayload(normalizedSteps),
    };

    const payload = {
      type: "SEQUENCE",
      name: trimmedName,
      content,
      ownerId,
      userId,
      workspaceId,
    };

    const editingId = !isCreate ? String(existingTemplate?._id || templateId || "") : "";
    const res = editingId
      ? await dispatch(
          updateTemplate({
            ...payload,
            templateId: editingId,
          })
        )
      : await dispatch(createTemplate(payload));

    if (res.meta.requestStatus !== "fulfilled") {
      toast.error(String(res.payload || "Failed to save template"));
      return;
    }

    toast.success(editingId ? "Sequence template updated" : "Sequence template saved");
    if (ownerId) {
      try {
        const list = await fetchTemplates(ownerId, userId, workspaceId);
        dispatch(setTemplates(list));
      } catch {
        // Redux already has the saved template from the save action
      }
    }

    const savedId = (res.payload as { _id?: string } | undefined)?._id;
    if (savedId && templateId !== savedId) {
      setHydratedId(String(savedId));
      navigate(`/templates/${savedId}`, { replace: true });
    }
  };

  return (
    <section className="noScroll h-full overflow-y-auto overflow-x-hidden bg-paper px-4 py-6 sm:px-9 sm:py-9">
      <div className="mx-auto max-w-[1600px]">
        <header className="mb-6">
          <h1 className="text-[27px] font-semibold tracking-tight text-dark-blue">
            Sequence template
          </h1>
          <p className="mt-1 text-sm text-muted">
            Preconfigured flow. Set the action order, delays, and message content here.
          </p>
        </header>

        <div className="mb-3.5 flex flex-wrap items-center gap-2">
          <Input
            size="md"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Template name"
            aria-label="Template name"
            containerClassName="mr-auto w-full max-w-md"
          />
          <div className="relative" ref={menuRef}>
            <Button
              variant="primary"
              size="sm"
              className="h-10 whitespace-nowrap px-3.5 font-semibold text-dark-blue"
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              + Add action
            </Button>
            {isMenuOpen ? (
              <div className="absolute right-0 z-20 mt-2 w-[260px] rounded-[8px] border border-line bg-white shadow-sm">
                <div className="border-b border-line px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted">
                  Actions
                </div>
                {menuOptions.map((option) => {
                  const emailLocked =
                    option.actionType === "sendEmail" && !emailChannelVerified;
                  return (
                    <button
                      key={option.actionType}
                      type="button"
                      className={[
                        "flex w-full flex-col items-start px-4 py-2.5 text-left hover:bg-paper",
                        emailLocked ? "cursor-not-allowed opacity-50" : "",
                      ].join(" ")}
                      onClick={() => addAction(option.actionType)}
                    >
                      <span className="text-sm font-semibold text-dark-blue">{option.label}</span>
                      <span className="text-[11px] text-muted">
                        {emailLocked
                          ? "Verify email in Integrations to enable"
                          : option.description}
                      </span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="flex w-full flex-col items-start px-4 py-2.5 text-left hover:bg-paper"
                  onClick={addDelay}
                >
                  <span className="text-sm font-semibold text-dark-blue">Delay</span>
                  <span className="text-[11px] text-muted">Wait before the next action</span>
                </button>
              </div>
            ) : null}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-10 !border-line px-3.5 font-semibold text-dark-blue hover:bg-paper"
            isLoading={loading}
            onClick={() => void handleSave()}
          >
            Save template
          </Button>
        </div>

        <div className="grid min-h-[700px] overflow-hidden rounded-[10px] border border-line lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="flow-canvas-dots flex flex-col items-center overflow-auto px-5 py-9 sm:px-8">
            {steps.length === 0 ? (
              <p className="max-w-[280px] text-center text-[11px] text-muted">
                Add Send Invitation, Visit Profile, Send Message, or Email to build this sequence.
              </p>
            ) : null}

            {steps.map((step, index) => {
              const meta = getSequenceActionMeta(step.actionType);
              const isSelected = step.id === selectedStepId;
              return (
                <div key={step.id} className="flex w-full flex-col items-center">
                  {index > 0 ? (
                    <>
                      <div className="my-1.5 h-6 border-l border-flow-connector" />
                      <button
                        type="button"
                        className="mb-1 rounded-[8px] border border-line bg-white px-[15px] py-2 text-center text-[11px] text-dark-blue shadow-sm"
                        onClick={() => {
                          setSelectedStepId(step.id);
                          setDelayModalStepId(step.id);
                        }}
                      >
                        {formatSequenceDelayLabel(step.delayDays, step.delayUnit)}
                        {step.delayDays > 0 &&
                        (step.actionType === "sendInMail" ||
                          step.actionType === "sendConnectionRequest") ? (
                          <small className="mt-0.5 block text-[9px] text-muted">
                            {step.continueIfNoReply ? "Only if no reply" : "Always continue"}
                          </small>
                        ) : null}
                      </button>
                      <div className="my-1.5 h-6 border-l border-flow-connector" />
                    </>
                  ) : null}
                  <article
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedStepId(step.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") setSelectedStepId(step.id);
                    }}
                    className={[
                      "relative w-[min(410px,90%)] rounded-[10px] border bg-white px-4 py-3 shadow-sm",
                      isSelected ? "border-bright-green" : "border-flow-node-border",
                    ].join(" ")}
                  >
                    <b className="block pr-6 text-sm font-semibold text-dark-blue">{meta?.label}</b>
                    <span className="mt-0.5 block text-[10px] text-muted">
                      {getSequenceStepSubtitle(step)}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove ${meta?.label || "action"}`}
                      className="absolute right-3 top-3 text-base leading-none text-muted hover:text-danger"
                      onClick={(event) => {
                        event.stopPropagation();
                        removeStep(step.id);
                      }}
                    >
                      ×
                    </button>
                  </article>
                </div>
              );
            })}

            {pendingDelay ? (
              <>
                <div className="my-1.5 h-6 border-l border-flow-connector" />
                <button
                  type="button"
                  className="mb-1 rounded-[8px] border border-line bg-white px-[15px] py-2 text-center text-[11px] text-dark-blue shadow-sm"
                  onClick={() => setIsPendingDelayModalOpen(true)}
                >
                  {formatSequenceDelayLabel(pendingDelay.delayDays, pendingDelay.delayUnit)}
                  <small className="mt-0.5 block text-[9px] text-muted">Before next action</small>
                </button>
              </>
            ) : null}

            <div className="my-1.5 h-6 border-l border-flow-connector" />
            <div className="mt-1 rounded-[8px] bg-sidebar-chip px-[18px] py-2.5 text-[11px] text-white">
              End sequence · stop on reply
            </div>
          </div>

          <aside className="overflow-y-auto border-t border-line bg-white p-[18px] lg:border-l lg:border-t-0">
            <h2 className="text-sm font-semibold text-dark-blue">Action configuration</h2>
            <p className="mt-1 text-[11px] text-muted">
              {selectedMeta?.label || "Select an action on the canvas"}
            </p>

            {selectedStep && selectedMeta ? (
              <>
                {selectedStep.actionType === "sendEmail" ? (
                  <div className="mt-5">
                    <Input
                      size="sm"
                      label="Subject"
                      labelClassName="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted"
                      value={selectedStep.subject}
                      maxLength={EMAIL_SUBJECT_MAX_LENGTH}
                      placeholder={DEFAULT_EMAIL_SUBJECT}
                      error={getSequenceStepSubjectError(selectedStep)}
                      onChange={(event) =>
                        updateStep(selectedStep.id, { subject: event.target.value })
                      }
                      onBlur={() => {
                        const normalized = normalizeSingleBraceMergeTokens(
                          selectedStep.subject,
                          INVITE_MERGE_TOKEN_KEYS
                        );
                        if (normalized !== selectedStep.subject) {
                          updateStep(selectedStep.id, { subject: normalized });
                        }
                      }}
                    />
                  </div>
                ) : null}

                {stepSupportsMessage(selectedStep.actionType) ? (
                  <div className={selectedStep.actionType === "sendEmail" ? "mt-4" : "mt-5"}>
                    <SequenceStepMessageField
                      key={selectedStep.id}
                      actionType={selectedStep.actionType}
                      value={selectedStep.message}
                      sendWithoutNote={selectedStep.sendWithoutNote}
                      onChange={(next) => updateStep(selectedStep.id, { message: next })}
                      onSendWithoutNoteChange={(checked) =>
                        updateStep(selectedStep.id, { sendWithoutNote: checked })
                      }
                    />
                  </div>
                ) : null}

                {selectedStep.actionType === "sendEmail" && !emailChannelVerified ? (
                  <p className="mt-4 text-[11px] text-danger">
                    {EMAIL_CHANNEL_REQUIRED_MESSAGE}{" "}
                    <Link to="/integrations" className="font-semibold underline">
                      Open Integrations
                    </Link>
                  </p>
                ) : null}

                <p className="mb-2 mt-5 text-[10px] font-semibold uppercase tracking-wide text-muted">
                  Send only when
                </p>
                <p className="text-[11px] text-flow-config-text">
                  {selectedStep.actionType === "sendEmail"
                    ? EMAIL_SEND_WHEN_COPY
                    : LINKEDIN_SEND_WHEN_COPY}
                </p>

                {steps[0]?.id !== selectedStep.id &&
                selectedStep.delayDays > 0 &&
                (selectedStep.actionType === "sendInMail" ||
                  selectedStep.actionType === "sendConnectionRequest") ? (
                  <>
                    <p className="mb-2 mt-5 text-[10px] font-semibold uppercase tracking-wide text-muted">
                      After sending
                    </p>
                    <Checkbox
                      checked={selectedStep.continueIfNoReply}
                      onChange={(checked) =>
                        updateStep(selectedStep.id, { continueIfNoReply: checked })
                      }
                      label="Continue only if no reply"
                      labelClassName="text-[11px] text-flow-config-text"
                    />
                  </>
                ) : null}

                {steps[0]?.id !== selectedStep.id ? (
                  <div className="mt-5 space-y-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted">
                      Wait before this action
                    </p>
                    <div className="grid grid-cols-[1fr_120px] gap-2">
                      <Input
                        size="sm"
                        type="number"
                        min={0}
                        max={SEQUENCE_DELAY_LIMITS[selectedStep.delayUnit].max}
                        value={String(selectedStep.delayDays)}
                        onChange={(event) =>
                          updateStep(selectedStep.id, {
                            delayDays: Math.floor(Number(event.target.value) || 0),
                          })
                        }
                      />
                      <Select
                        size="sm"
                        ariaLabel="Delay unit"
                        value={selectedStep.delayUnit}
                        options={SEQUENCE_DELAY_UNIT_OPTIONS.map((option) => ({
                          value: option.value,
                          label: option.label,
                        }))}
                        onChange={(next) => {
                          const nextUnit: SequenceDelayUnit =
                            next === "hours" ? "hours" : "days";
                          const nextValue =
                            selectedStep.delayDays <= 0
                              ? 0
                              : clampSequenceDelay(selectedStep.delayDays, nextUnit);
                          updateStep(selectedStep.id, {
                            delayUnit: nextUnit,
                            delayDays: nextValue,
                          });
                        }}
                      />
                    </div>
                    {selectedDelayError ? (
                      <p className="text-[11px] text-danger">{selectedDelayError}</p>
                    ) : (
                      <p className="text-[11px] text-muted">
                        0 is No Delay. Otherwise {SEQUENCE_DELAY_LIMITS.days.min}–
                        {SEQUENCE_DELAY_LIMITS.days.max} days or {SEQUENCE_DELAY_LIMITS.hours.min}–
                        {SEQUENCE_DELAY_LIMITS.hours.max} hours.
                      </p>
                    )}
                  </div>
                ) : null}

                <p className="mb-2 mt-5 text-[10px] font-semibold uppercase tracking-wide text-muted">
                  Daily sending capacity
                </p>
                <p className="text-[11px] text-flow-config-text">
                  Campaign daily limits still apply after this template is used.
                </p>
              </>
            ) : (
              <p className="mt-5 text-[11px] text-muted">
                Select Send Invitation, Send Message, or Email to configure its copy here. Visit
                Profile has no message content.
              </p>
            )}
          </aside>
        </div>
      </div>
      <DelayBeforeActionModal
        isOpen={
          isPendingDelayModalOpen ||
          Boolean(delayModalStep && steps[0]?.id !== delayModalStep.id)
        }
        onClose={() => {
          setDelayModalStepId(null);
          setIsPendingDelayModalOpen(false);
        }}
        initialValue={
          isPendingDelayModalOpen
            ? pendingDelay?.delayDays || SEQUENCE_DELAY_LIMITS.days.min
            : delayModalStep && delayModalStep.delayDays > 0
              ? delayModalStep.delayDays
              : SEQUENCE_DELAY_LIMITS.days.min
        }
        initialUnit={
          isPendingDelayModalOpen
            ? pendingDelay?.delayUnit || "days"
            : delayModalStep && delayModalStep.delayDays > 0
              ? delayModalStep.delayUnit
              : "days"
        }
        onSave={(nextValue, nextUnit) => {
          if (isPendingDelayModalOpen) {
            setPendingDelay({ delayDays: nextValue, delayUnit: nextUnit });
            setIsPendingDelayModalOpen(false);
            return;
          }
          if (!delayModalStep) return;
          updateStep(delayModalStep.id, { delayDays: nextValue, delayUnit: nextUnit });
          setDelayModalStepId(null);
        }}
      />
    </section>
  );
};

export default SequenceTemplateEditor;
